import React from 'react';

import styled from 'styled-components';

import AsyncStateWrapper from '@core/components/async-state';
import {
  BackdropRow,
  Box,
  ClayTable,
  Row,
  HiddenScrollColumn,
  ScrollableColumn,
  TableData,
  TableHeaderItem,
  TableRow,
  SortableHeader,
} from '@core/components/basic';
import { Smile, Frown } from '@core/components/icons';
import Pagination from '@core/components/pagination';
import PaginationSizeSelector, {
  sizeOptions,
} from '@core/components/pagination/pagination-size-selector';
import useRequestWithUrlPagination from '@core/hooks/pagination/use-request-with-url-pagination';
import { useListAssistantAnswersQuery } from '@core/api/school';
import ExportCsvButton from '@core/components/csv/export-button';
import Search from '@core/components/search';

const csvHeaders = [
  {
    label: 'ID',
    key: 'id',
  },
  {
    label: 'Author',
    key: 'author',
  },
  {
    label: 'Date',
    key: 'createdAt',
  },
  {
    label: 'Question',
    key: 'question',
  },
  {
    label: 'Answer',
    key: 'description',
  },
  {
    label: 'Useful?',
    key: 'useful',
  },
  {
    label: 'Feedback',
    key: 'feedback',
  },
];

const ListAssistantAnswers = () => {
  const {
    data: answers = [],
    pagination,
    ...rulesListAsyncState
  } = useRequestWithUrlPagination({
    request: useListAssistantAnswersQuery,
    initialSort: 'createdAt.desc',
  });

  return (
    <ScrollableColumn flex="1 1 auto" $width="100%" alignItems="center">
      <Box $width="100%" p="smallMedium">
        <AsyncStateWrapper
          isLoading={rulesListAsyncState.isLoading}
          error={rulesListAsyncState.error?.data?.message}
        >
          <HiddenScrollColumn
            width="100%"
            alignItems="center"
            flex={1}
            overflowY="scroll"
          >
            <BackdropRow
              justifyContent="space-around"
              px="medium"
              width="100%"
              py="smallMedium"
            >
              <Search
                initialValue={pagination.search}
                onSearch={pagination.changeSearch}
              />
              <Row flex={1} justifyContent="flex-end" $gap="small">
                {answers && (
                  <ExportCsvButton
                    file="assistant_answers"
                    data={answers}
                    headers={csvHeaders}
                  />
                )}
                <PaginationSizeSelector
                  onChange={pagination.changeSize}
                  size={pagination.size}
                  options={sizeOptions}
                />
              </Row>
            </BackdropRow>

            <ScrollableColumn $width="100%" p="smallMedium">
              <ClayTable borderRadius="large" $height="fit-content">
                <thead>
                  <TableRow variant="header" $width="100%" position="sticky">
                    <TableHeaderItem>
                      <SortableHeader
                        label="Question"
                        field="question"
                        onSort={pagination.changeSort}
                        currentSort={pagination.sort}
                      />
                    </TableHeaderItem>
                    <TableHeaderItem>
                      <SortableHeader
                        label="Answer"
                        field="description"
                        onSort={pagination.changeSort}
                        currentSort={pagination.sort}
                      />
                    </TableHeaderItem>
                    <TableHeaderItem>
                      <SortableHeader
                        label="Author"
                        field="author"
                        onSort={pagination.changeSort}
                        currentSort={pagination.sort}
                      />
                    </TableHeaderItem>
                    <TableHeaderItem>Useful</TableHeaderItem>
                    <TableHeaderItem>Feedback</TableHeaderItem>
                    <TableHeaderItem>
                      <SortableHeader
                        label="Asked On"
                        field="createdAt"
                        onSort={pagination.changeSort}
                        currentSort={pagination.sort}
                      />
                    </TableHeaderItem>
                  </TableRow>
                </thead>

                <tbody>
                  {answers?.map(
                    (
                      {
                        id,
                        question,
                        description,
                        useful,
                        feedback,
                        author,
                        createdAt,
                      },
                      index
                    ) => (
                      <TableRow
                        key={id}
                        variant={index % 2 === 0 ? 'dark' : 'light'}
                      >
                        <WhiteLineTableData>{question}</WhiteLineTableData>
                        <WhiteLineTableData>{description}</WhiteLineTableData>
                        <TableData>{author}</TableData>
                        <TableData>
                          {useful === true && <Smile />}
                          {useful === false && <Frown />}
                        </TableData>
                        <WhiteLineTableData>{feedback}</WhiteLineTableData>
                        <TableData>{createdAt}</TableData>
                      </TableRow>
                    )
                  )}
                </tbody>
              </ClayTable>
            </ScrollableColumn>
            <Pagination pagination={pagination} />
          </HiddenScrollColumn>
        </AsyncStateWrapper>
      </Box>
    </ScrollableColumn>
  );
};

const WhiteLineTableData = styled(TableData)`
  white-space: pre-line;
`;

export default ListAssistantAnswers;
